<script setup lang="ts">
  // Framework
  import { IN_BROWSER, isNull, isNullOrUndefined, useBreakpoints, useLogger } from '@vuetify/v0'

  // Context
  import { useDiscoveryRootContext } from './DiscoveryRoot.vue'

  // Composables
  import { useDiscovery } from '@/composables/useDiscovery'
  import { useSettings } from '@/composables/useSettings'

  // Utilities
  import { nextTick, onBeforeUnmount, shallowRef, toRef, useAttrs, useTemplateRef, watch } from 'vue'

  defineOptions({ name: 'DiscoveryContent', inheritAttrs: false })

  const attrs = useAttrs()
  const logger = useLogger()

  const {
    placement = 'bottom',
    placementMobile,
    offset = 16,
    noOverflow = false,
  } = defineProps<{
    placement?: string
    placementMobile?: string
    offset?: number
    noOverflow?: boolean
  }>()

  const breakpoints = useBreakpoints()

  // Mobile fallback placement, measured per step. A vertical anchor edge alone
  // can't keep the card on-screen: a full-height target (Ask AI panel, expanded
  // code pane) leaves no room on the anchored side and `bottom: anchor(top)`
  // pushes the card past the viewport edge.
  const flip = shallowRef<string | null>(null)

  // Room a card needs on the anchored side: max-height is unknowable before
  // render, so budget for a typical tall card (20rem wide, ~15rem tall) + offset.
  const ROOM = 256

  function measure (el: HTMLElement) {
    flip.value = null
    if (!breakpoints.smAndDown.value) return

    const requested = isNullOrUndefined(placementMobile) ? placement : placementMobile
    if (requested !== 'top' && requested !== 'bottom') return

    const rect = el.getBoundingClientRect()
    const vh = window.innerHeight

    // A target most of the viewport tall has no roomy side; overlaying it
    // centered keeps the card (and its buttons) reachable.
    if (rect.height >= vh * 0.6) {
      flip.value = 'center'
      return
    }

    const room = { top: rect.top, bottom: vh - rect.bottom }
    if (room[requested] >= ROOM) return

    const other = requested === 'top' ? 'bottom' : 'top'
    flip.value = room[other] >= ROOM ? other : 'center'
  }

  const activePlacement = toRef(() => {
    // Last step always uses center placement
    if (discovery.isLast.value) return 'center'
    if (breakpoints.smAndDown.value) {
      if (!isNull(flip.value)) return flip.value
      if (!isNullOrUndefined(placementMobile)) return placementMobile
    }
    return placement
  })

  const root = useDiscoveryRootContext('v0:discovery')
  const discovery = useDiscovery()
  const settings = useSettings()
  const isReady = shallowRef(false)
  const contentRef = useTemplateRef<HTMLElement>('content')

  // Check for CSS Anchor Positioning support
  // Safari/iOS don't support anchor positioning - check for the specific property
  const supportsAnchor = IN_BROWSER
    && typeof CSS !== 'undefined'
    && typeof CSS.supports === 'function'
    && CSS.supports('position-area', 'top')

  // Map placement to position-area values with self-alignment for centering
  const placementStyles: Record<string, Record<string, string>> = {
    bottom: {
      positionArea: 'bottom',
      justifySelf: 'anchor-center',
    },
    top: {
      positionArea: 'top',
      justifySelf: 'anchor-center',
    },
    left: {
      positionArea: 'left',
      alignSelf: 'anchor-center',
    },
    right: {
      positionArea: 'right',
      alignSelf: 'anchor-center',
    },
  }

  const style = toRef(() => {
    const currentPlacement = activePlacement.value

    // The last step has no activator, so no element carries the anchor-name.
    // position-area: center silently no-ops without a resolvable anchor and the
    // inset collapses the box to the top-left corner, so center on the viewport.
    // Center via inset + margin auto rather than translate(-50%, -50%): the enter
    // animation scales through the `scale` property, and a positioning translate
    // shares its transform-origin, pinning the scale's fixed point to the box's
    // own center - which sits at the visual bottom-right, so the box grew from
    // there instead of its center. No transform here leaves scale about center.
    if (currentPlacement === 'center') {
      return {
        position: 'fixed' as const,
        inset: '0',
        margin: 'auto',
        width: 'max-content',
        height: 'max-content',
        maxWidth: `min(20rem, calc(100vw - ${offset * 2}px))`,
        maxHeight: `calc(100vh - ${offset * 2}px)`,
        overflow: noOverflow ? 'visible' : 'auto',
      }
    }

    if (supportsAnchor) {
      const base = {
        position: 'fixed' as const,
        width: 'max-content',
        height: 'max-content',
        maxWidth: `min(20rem, calc(100vw - ${offset * 2}px))`,
        maxHeight: `calc(100vh - ${offset * 2}px)`,
        overflow: noOverflow ? 'visible' : 'auto',
        positionAnchor: `--discovery-${root.step}`,
      }

      // On mobile, only anchor the popover's vertical edge to the target and keep
      // it centered in the viewport horizontally. A narrow screen has no room for
      // left/right placement (those collapse to top/bottom via placementMobile),
      // and position-area centers on the anchor's own box - a target pinned to (or
      // overflowing) a screen edge would drag the popover off-screen. Decoupling
      // the horizontal axis keeps the box fully visible regardless of where its
      // target sits.
      if (breakpoints.smAndDown.value && (currentPlacement === 'top' || currentPlacement === 'bottom')) {
        const vertical = currentPlacement === 'bottom'
          ? { top: 'anchor(bottom)', marginTop: `${offset}px` }
          : { bottom: 'anchor(top)', marginBottom: `${offset}px` }
        return {
          ...base,
          left: '50%',
          transform: 'translateX(-50%)',
          ...vertical,
        }
      }

      return {
        ...base,
        inset: `${offset}px`,
        ...placementStyles[currentPlacement] ?? placementStyles.bottom,
      }
    }
    // Fallback for browsers without anchor positioning (Safari/iOS)
    // Centers popover at viewport edge since we can't anchor to elements
    // Reset inset to avoid top-layer default positioning
    const base = {
      inset: 'auto',
      position: 'fixed' as const,
      maxWidth: `calc(100vw - ${offset * 2}px)`,
      maxHeight: `calc(100vh - ${offset * 2}px)`,
      overflow: noOverflow ? 'visible' : 'auto',
    }
    const fallbackStyles: Record<string, Record<string, string>> = {
      bottom: { bottom: `${offset}px`, left: '50%', transform: 'translateX(-50%)' },
      top: { top: `${offset}px`, left: '50%', transform: 'translateX(-50%)' },
      left: { top: '50%', left: `${offset}px`, transform: 'translateY(-50%)' },
      right: { top: '50%', right: `${offset}px`, transform: 'translateY(-50%)' },
    }
    return {
      ...base,
      ...fallbackStyles[currentPlacement] ?? fallbackStyles.bottom,
    }
  })

  // Unmount guard for rAF polling
  let isUnmounted = false
  onBeforeUnmount(() => {
    isUnmounted = true
  })

  // Poll for activator to be registered, then show content
  watch(
    root.isActive,
    isActive => {
      if (!IN_BROWSER) return

      if (isActive) {
        isReady.value = false

        const step = discovery.steps.get(root.step)
        if (step?.noActivator || discovery.isLast.value) {
          isReady.value = true
          return
        }

        const startTime = performance.now()
        const TIMEOUT_MS = 2000

        // Poll until activator is registered and DOM element exists (with timeout)
        function checkActivator () {
          // A stale loop can outlive its step (next/back/exit while polling)
          if (isUnmounted || !root.isActive.value) return

          const activator = discovery.activators.get(root.step)
          if (activator?.element?.value) {
            measure(activator.element.value)
            // Activator found - wait one more frame for anchor-name CSS
            requestAnimationFrame(() => {
              if (!isUnmounted && root.isActive.value) isReady.value = true
            })
          } else if (performance.now() - startTime > TIMEOUT_MS) {
            logger.warn(`[DiscoveryContent] Activator for step "${root.step}" not found after ${TIMEOUT_MS}ms`)
            // Timeout - show content anyway to avoid hanging
            isReady.value = true
          } else {
            // Keep polling
            requestAnimationFrame(checkActivator)
          }
        }
        requestAnimationFrame(checkActivator)
      } else {
        isReady.value = false
        flip.value = null
      }
    },
    { immediate: true },
  )

  watch(isReady, ready => {
    if (ready) {
      nextTick(() => {
        // Don't steal focus from input elements (for interactive tours)
        const active = document.activeElement
        const isInputFocused = active?.tagName === 'INPUT' ||
          active?.tagName === 'TEXTAREA' ||
          active?.getAttribute('contenteditable') === 'true'
        if (!isInputFocused) {
          contentRef.value?.focus()
        }
      })
    }
  })
</script>

<template>
  <Teleport v-if="isReady" to="body">
    <div
      ref="content"
      v-bind="attrs"
      :class="['outline-none', { 'discovery-content': !settings.userPrefersReducedMotion.value }]"
      :style="{
        ...style,
        zIndex: 9999,
      }"
      tabindex="-1"
    >
      <slot />
    </div>
  </Teleport>
</template>

<style scoped>
.discovery-content {
  scale: 1;
  opacity: 1;
  transition:
    scale 0.25s cubic-bezier(0.17, 0.89, 0.32, 1.28),
    opacity 0.15s ease-out;

  @starting-style {
    scale: 0.92;
    opacity: 0;
  }
}
</style>
